import { defineStore } from 'pinia'
import { ref } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { AdventurersProgressResponse, AdventurerProgressByWeekResponse, ExpeditionCreatePayload } from '@/types/expedition'

export const useExpeditionsStore = defineStore('expeditions', () => {
  const expeditions = ref<any[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const latestExpeditionId = ref<string | null>(null)
  const adventurersProgress = ref<AdventurersProgressResponse | null>(null)
  const adventurerWeeklyProgress = ref<AdventurerProgressByWeekResponse | null>(null)
  const currentWeek = ref<any | null>(null)
  const userData = ref<any | null>(null)
  const expeditionTypes = ref<any[]>([])
  const expeditionData = ref<any | null>(null)
  const userExpeditionData = ref<any | null>(null)

  const fetchExpeditions = async () => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITIONS_LIST, { params: { page_size: 1000 } })
      if (response.data?.results && response.data.results.length > 0) {
        expeditions.value = response.data.results
        latestExpeditionId.value = response.data.results[response.data.results.length - 1].id
      } else {
        expeditions.value = []
        latestExpeditionId.value = null
      }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch expeditions'
    } finally {
      isLoading.value = false
    }
  }

  const fetchAdventurersProgress = async (expeditionId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_ADVENTURERS_PROGRESS(expeditionId))
      adventurersProgress.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch adventurers progress'
    } finally {
      isLoading.value = false
    }
  }

  const fetchCurrentWeek = async (expeditionId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_CURRENT_WEEK_BY_EXPEDITION_ID(expeditionId))
      currentWeek.value = response.data.current_week
      userData.value = response.data.user
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch current week'
    } finally {
      isLoading.value = false
    }
  }

  const fetchAdventurerProgressByWeek = async (expeditionId: string, userId: string, weekId: string) => {
    isLoading.value = true
    error.value = null
    adventurerWeeklyProgress.value = null
    try {
      const response = await api.get(API_ROUTES.GET_ADVENTURER_PROGRESS_BY_WEEK(expeditionId, userId, weekId))
      adventurerWeeklyProgress.value = response.data
      userData.value = response.data.user

    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch adventurer progress by week'
      adventurerWeeklyProgress.value = null
    } finally {
      isLoading.value = false
    }
  }

  const fetchExpeditionTypes = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITION_TYPES)
      expeditionTypes.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch expedition types'
    } finally {
      isLoading.value = false
    }
  }

  const createExpedition = async (payload: ExpeditionCreatePayload) => {
    isLoading.value = true
    error.value = null
    try {
      await api.post(API_ROUTES.CREATE_EXPEDITION, payload)
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to create expedition'
    } finally {
      isLoading.value = false
    }
  }

  const fetchExpeditionData = async (expeditionId: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITION_BY_ID(expeditionId))
      expeditionData.value = response.data
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch expedition data'
    } finally {
      isLoading.value = false
    }
  }

  const fetchExpeditionByUserId = async (userId: string) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITION_BY_USER_ID(userId))
      userExpeditionData.value = response.data
    }
    catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch expedition by user id'
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if the current user has an active expedition.
   * Uses GET /api/v1/curriculum/user/{userId}/expedition/ which returns
   * the user's active expedition (is_active=True) or id: null if none.
   */
  const checkHasActiveExpedition = async (userId: number): Promise<boolean> => {
    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITION_BY_USER_ID(String(userId)))
      const data = response.data as { id?: number | null; is_active?: boolean }
      return !!(data?.id && data?.is_active !== false)
    } catch {
      return false
    }
  }

  return {
    expeditions,
    isLoading,
    error,
    fetchExpeditions,
    latestExpeditionId,
    adventurersProgress,
    fetchAdventurersProgress,
    adventurerWeeklyProgress,
    fetchAdventurerProgressByWeek,
    currentWeek,
    fetchCurrentWeek,
    userData,
    expeditionTypes,
    fetchExpeditionTypes,
    createExpedition,
    expeditionData,
    fetchExpeditionData,
    userExpeditionData,
    fetchExpeditionByUserId,
    checkHasActiveExpedition
  }
})
